import React from "react";
import { Link } from "react-router-dom";
import {
  User,
  Wallet,
  Receipt,
  BarChart3,
  Sliders,
  Lightbulb,
  FileDown,
  RotateCcw,
  Languages,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { useBudget } from "../context/BudgetContext";
import { useI18n } from "../context/I18nContext";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { exportPdf } from "../lib/pdf";

const NAV_ITEMS = [
  { id: "client", icon: User },
  { id: "revenus", icon: Wallet },
  { id: "depenses", icon: Receipt },
  { id: "analyse", icon: BarChart3 },
  { id: "optimisation", icon: Sliders },
  { id: "recommandations", icon: Lightbulb },
];

export const Sidebar = ({ active, onChange }) => {
  const { t, lang, toggleLang } = useI18n();
  const { completion, state, indicators, diagnostic, optim, recommendations, reset } = useBudget();

  const handleExport = () => {
    exportPdf({ state, indicators, diagnostic, optim, recommendations, t, lang });
    toast.success(t.toasts.pdfExported);
  };

  const handleReset = () => {
    if (window.confirm(t.toasts.resetConfirm)) {
      reset();
      toast.success(t.toasts.reset);
    }
  };

  return (
    <aside
      data-testid="sidebar"
      className="hidden md:flex w-64 fixed h-screen left-0 top-0 flex-col gap-2 p-5 bg-white/60 backdrop-blur-2xl border-r border-white/50 shadow-[4px_0_24px_rgba(0,0,0,0.02)] z-40"
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-1 py-4" data-testid="app-logo">
        <div className="w-10 h-10 rounded-xl bg-indigo-950 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-white" />
        </div>
        <div className="flex flex-col leading-tight">
          <span
            className="text-lg font-medium tracking-tight text-indigo-950"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            BudgetLens
          </span>
          <span className="text-xs text-slate-500">{t.appTagline}</span>
        </div>
      </div>

      <nav className="flex-1 mt-4 flex flex-col gap-1" aria-label="Primary">
        {NAV_ITEMS.map(({ id, icon: Icon }) => {
          const isActive = active === id;
          const done = completion[id];
          return (
            <button
              key={id}
              data-testid={`nav-${id}`}
              onClick={() => onChange(id)}
              className={cn(
                "group flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200",
                isActive
                  ? "bg-indigo-950 text-white shadow-md"
                  : "text-slate-700 hover:bg-white/90 hover:text-indigo-950"
              )}
            >
              <Icon className={cn("w-4 h-4 shrink-0", isActive ? "text-white" : "text-slate-500 group-hover:text-indigo-950")} />
              <span className="text-sm font-medium flex-1">{t.modules[id]}</span>
              {done ? (
                <CheckCircle2
                  className={cn("w-4 h-4", isActive ? "text-emerald-300" : "text-emerald-600")}
                  data-testid={`completed-${id}`}
                />
              ) : (
                <Circle className={cn("w-3.5 h-3.5", isActive ? "text-white/40" : "text-slate-300")} />
              )}
            </button>
          );
        })}
      </nav>

      <div className="flex flex-col gap-2 pt-4 border-t border-slate-200/80">
        <button
          data-testid="btn-export-pdf"
          onClick={handleExport}
          className="flex items-center justify-center gap-2 bg-indigo-950 text-white hover:bg-indigo-900 rounded-xl px-4 py-2.5 font-medium text-sm transition-all shadow-sm hover:shadow-md active:scale-95"
        >
          <FileDown className="w-4 h-4" />
          {t.nav.exportPdf}
        </button>
        <div className="flex items-center gap-2">
          <button
            data-testid="btn-toggle-lang"
            onClick={toggleLang}
            className="flex-1 flex items-center justify-center gap-2 bg-white/80 text-indigo-950 border border-slate-200 hover:bg-slate-50 rounded-xl px-3 py-2 font-medium text-sm transition-all"
            aria-label={t.nav.language}
          >
            <Languages className="w-4 h-4" />
            <span className="uppercase tracking-wider">{lang}</span>
          </button>
          <button
            data-testid="btn-reset"
            onClick={handleReset}
            className="flex items-center justify-center gap-2 bg-white/80 text-rose-700 border border-rose-100 hover:bg-rose-50 rounded-xl px-3 py-2 font-medium text-sm transition-all"
            aria-label={t.nav.reset}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export const MobileNav = ({ active, onChange }) => {
  const { t, lang, toggleLang } = useI18n();
  const { completion, state, indicators, diagnostic, optim, recommendations, reset } = useBudget();

  const handleExport = () => {
    exportPdf({ state, indicators, diagnostic, optim, recommendations, t, lang });
    toast.success(t.toasts.pdfExported);
  };
  const handleReset = () => {
    if (window.confirm(t.toasts.resetConfirm)) {
      reset();
      toast.success(t.toasts.reset);
    }
  };

  return (
    <>
      {/* Top compact header */}
      <header
        data-testid="mobile-header"
        className="md:hidden sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200/70 px-4 py-3 flex items-center justify-between gap-3"
      >
        <Link to="/" className="flex items-center gap-2" data-testid="mobile-logo-home">
          <div className="w-8 h-8 rounded-lg bg-indigo-950 flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span
              className="text-sm font-medium tracking-tight text-indigo-950"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              BudgetLens
            </span>
            <span className="text-[10px] text-slate-500">{t.modules[active]}</span>
          </div>
        </Link>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="mobile-btn-lang"
            onClick={toggleLang}
            className="flex items-center justify-center w-9 h-9 rounded-lg text-slate-600 hover:text-indigo-950 hover:bg-slate-100 transition-all"
            aria-label={t.nav.language}
          >
            <Languages className="w-4 h-4" />
          </button>
          <button
            data-testid="mobile-btn-export"
            onClick={handleExport}
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-950 text-white hover:bg-indigo-900 transition-all"
            aria-label={t.nav.exportPdf}
          >
            <FileDown className="w-4 h-4" />
          </button>
          <button
            data-testid="mobile-btn-reset"
            onClick={handleReset}
            className="flex items-center justify-center w-9 h-9 rounded-lg text-rose-700 hover:bg-rose-50 transition-all"
            aria-label={t.nav.reset}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Bottom fixed tab bar */}
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/90 backdrop-blur-xl border-t border-slate-200/80 shadow-[0_-4px_20px_rgba(0,0,0,0.04)]"
        aria-label="Modules"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex items-stretch justify-between">
          {NAV_ITEMS.map(({ id, icon: Icon }) => {
            const isActive = active === id;
            const done = completion[id];
            return (
              <button
                key={id}
                data-testid={`mobile-tab-${id}`}
                onClick={() => onChange(id)}
                className="relative flex-1 flex flex-col items-center gap-0.5 py-2 px-1 min-w-0"
                aria-current={isActive ? "page" : undefined}
              >
                <span
                  className={cn(
                    "relative flex items-center justify-center w-10 h-7 rounded-full transition-all",
                    isActive ? "bg-indigo-950" : "bg-transparent"
                  )}
                >
                  <Icon
                    className={cn(
                      "w-[18px] h-[18px] transition-colors",
                      isActive ? "text-white" : "text-slate-500"
                    )}
                  />
                  {done && !isActive ? (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" />
                  ) : null}
                </span>
                <span
                  className={cn(
                    "text-[10px] font-medium tracking-tight truncate max-w-full",
                    isActive ? "text-indigo-950" : "text-slate-500"
                  )}
                >
                  {t.modules[id]}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};
